import React, {SyntheticEvent, useEffect, useMemo, useState} from 'react';
import {useSnippetContext} from "../Utils/UseSnippetContext";
import useServiceUrls from "../Utils/UseServiceUrls";
import {
    AccountIndicatorType,
    AvailableCoupon,
    CustomerDashboardSnippetProfile,
    LoyaltyAccountIndicator, WebSnippetProfile,
} from "../Helpers/Types";
import {useAuth} from "../Utils/UseAuth";
import QRCode from 'react-qr-code'
import DashboardCouponDisplay from "./DashboardCouponDisplay";
import useGetCustomerSession from "../Utils/UseGetCustomerSession";
import {MDBCol, MDBRow} from "mdbreact";
import SnippetHeader from "../Helpers/SnippetHeader";
import ReferralSnippet from "../Referral/ReferralSnippet";
import useSnippetConfig from "../Utils/UseSnippetConfig";
import LoyaltyAccountIndicatorCard from "rc-progress-bars/dist/LoyaltyAccountIndicators/LoyaltyAccountIndicatorCard";
import ProgressBarWithCheckpoints from "rc-progress-bars/dist/LoyaltyAccountIndicators/ProgressBarWithCheckpoints";
import SnippetFooter from "../Helpers/SnippetFooter";
import {Grid} from "@mui/material";
import CouponSection from "./CouponSection";
import useSaveMember from "../Utils/UseSaveMember";
import useApplyCoupon from "../Utils/UseApplyCoupon";
import useApplyCouponToCart from "../Utils/UseApplyCouponToCart";
import {checkRequiredSignupFields} from "../Helpers/Functions";
import SignupWrapper from "../SignupWrapper";
import useGetCartCustomerStatus from "../Utils/UseGetCartCustomerStatus";
import useSetSessionCustomerToCart from "../Utils/UseSetSessionCustomerToCart";

type DashboardProps = {
    snippetConfig: WebSnippetProfile
}

export default function Dashboard(props: DashboardProps) {
    // @ts-ignore
    const { customer, session } = useAuth()
    // @ts-ignore
    const { domain, deviceCode, onCompleteFunction, cartUid, cartExternalId, cartSourceExternalId } = useSnippetContext();
    const { serviceUrls } = useServiceUrls(domain);
    const { customerSession, status } = useGetCustomerSession(serviceUrls, session, domain, deviceCode)
    const { SnippetConfigStatus, SnippetConfig } = useSnippetConfig(serviceUrls, domain, deviceCode, props.snippetConfig.CustomerDashboard?.ReferralSnippetCode ?? '', (props.snippetConfig.CustomerDashboard?.ReferralSnippetCode ?? '') !== '');
    
    // Load signup snippet config for validation
    // If UniversalSignupSnippetCode is empty, skip validation and load dashboard normally
    const universalSignupSnippetCode = props.snippetConfig.UniversalSignupSnippetCode ?? '';
    const shouldLoadSignupConfig = universalSignupSnippetCode !== '';
    const { SnippetConfig: signupSnippetConfig, SnippetConfigStatus: signupConfigStatus } = useSnippetConfig(serviceUrls, domain, deviceCode, universalSignupSnippetCode, shouldLoadSignupConfig);
    
    const [showReferral, setShowReferral] = useState(false)
    const [onCompleteExists, setOnCompleteExists] = useState(false)
    const [couponCode, setCouponCode] = useState<string>('')
    const [showSignupSnippet, setShowSignupSnippet] = useState(false)
    const [hasAttemptedSetCustomer, setHasAttemptedSetCustomer] = useState(false)
    const [applyingCouponCode, setApplyingCouponCode] = useState<string | null>(null)
    
    const qrValue = useMemo(() => props.snippetConfig.CustomerDashboard?.QRCustomerField, [props.snippetConfig])
    const dashboardProfile = useMemo(() => props.snippetConfig.CustomerDashboard, [props.snippetConfig])
    
    const [apply, {status: applyStatus, data: applyData, reset, error}] = useApplyCoupon()
    const [applyToCart, {status: applyToCartStatus, data: applyToCartData, reset: resetCartCoupon, error: applyToCartError}] = useApplyCouponToCart()
    
    // Get cart if cart identifiers are available
    const hasCartIdentifiers = !!(cartUid || cartExternalId || cartSourceExternalId);
    // URI encode cartExternalId when using a cart (with the included key)
    const encodedCartExternalId = useMemo(() => {
        if (hasCartIdentifiers && cartExternalId) {
            return encodeURIComponent(cartExternalId);
        }
        return cartExternalId ?? '';
    }, [hasCartIdentifiers, cartExternalId]);
    const { customerStatus: cartStatus, isSessionCustomer, refetch: refetchCart } = useGetCartCustomerStatus(serviceUrls, domain, deviceCode, session, cartUid ?? '', hasCartIdentifiers);
    
    // Mutation hook for setting customer to cart
    const [setCustomerToCart, { status: setCustomerStatus, error: setCustomerError }] = useSetSessionCustomerToCart()
    
    useEffect(() => {
        // @ts-ignore
        if (typeof window[onCompleteFunction] === "function") {
            setOnCompleteExists(true)
            return;
        }

        console.log(`OnCompleteFunction ${onCompleteFunction} does not exist`)
    }, [])
    
    useEffect(() => {
        const isSuccess = hasCartIdentifiers ? applyToCartStatus === 'success' : applyStatus === 'success';
        if (isSuccess && onCompleteExists && couponCode !== '') {
            // @ts-ignore
            window[onCompleteFunction]({CouponCode: couponCode});
        }
        if (couponCode !== '' && isSuccess)
            setCouponCode('')
    }, [couponCode, applyStatus, applyToCartStatus, hasCartIdentifiers, onCompleteExists, onCompleteFunction])
    
    // Clear applying coupon code after success/error in cart mode
    useEffect(() => {
        if (hasCartIdentifiers && applyingCouponCode) {
            if (applyToCartStatus === 'success' || applyToCartStatus === 'error') {
                // Clear applying state after a delay to show success/error message
                const timer = setTimeout(() => {
                    setApplyingCouponCode(null);
                }, 2000);
                return () => clearTimeout(timer);
            }
        }
    }, [applyToCartStatus, hasCartIdentifiers, applyingCouponCode])
    
    // Check required fields and redirect/show signup snippet if needed
    // Only runs if UniversalSignupSnippetCode is configured
    useEffect(() => {
        // Only check if we have customer, signup config loaded, and haven't already shown signup
        // If shouldLoadSignupConfig is false (empty UniversalSignupSnippetCode), skip validation
        if (!customer || !shouldLoadSignupConfig || signupConfigStatus !== 'success' || !signupSnippetConfig || showSignupSnippet) {
            return;
        }
        
        const signupConfig = signupSnippetConfig.CustomerSignup ?? signupSnippetConfig;
        const hasAllRequiredFields = checkRequiredSignupFields(customer, signupConfig);
        
        if (!hasAllRequiredFields) {
            // Check if this is embedded (has data-bloyal attributes) or standalone
            const isEmbedded = document.querySelector('[data-bloyal-snippet-code]') !== null;
            
            if (isEmbedded) {
                // For embedded snippets, show signup snippet inline
                setShowSignupSnippet(true);
            } else {
                // For standalone/hosted snippets, redirect to signup snippet with return URL and session key
                const currentUrl = window.location.href;
                const returnUrl = encodeURIComponent(currentUrl);
                // Include session key in URL so AuthProvider can pick it up
                const sessionParam = session ? `&bl_sk=${encodeURIComponent(session)}` : '';
                const signupUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?bl_snippetcode=${universalSignupSnippetCode}&bl_logindomain=${domain}&bl_returnurl=${returnUrl}${sessionParam}`;
                window.location.assign(signupUrl);
            }
        }
    }, [customer, signupConfigStatus, signupSnippetConfig, shouldLoadSignupConfig, universalSignupSnippetCode, domain, session, showSignupSnippet])
    
    // Reset showSignupSnippet when customer data changes (after successful signup)
    useEffect(() => {
        if (showSignupSnippet && customer && signupConfigStatus === 'success' && signupSnippetConfig) {
            const signupConfig = signupSnippetConfig.CustomerSignup ?? signupSnippetConfig;
            const hasAllRequiredFields = checkRequiredSignupFields(customer, signupConfig);
            if (hasAllRequiredFields) {
                setShowSignupSnippet(false);
            }
        }
    }, [customer, showSignupSnippet, signupConfigStatus, signupSnippetConfig])
    
    // Refetch cart after successfully setting customer
    useEffect(() => {
        if (setCustomerStatus === 'success' && refetchCart) {
            refetchCart();
        }
    }, [setCustomerStatus, refetchCart])
    
    // Sync cart customer with session customer if they don't match
    useEffect(() => {
        // Only proceed if cart is loaded, customer exists, mutation is not in progress, and we haven't already attempted/failed
        if (cartStatus !== 'success' || isSessionCustomer === undefined || !customer || setCustomerStatus === 'loading' || setCustomerStatus === 'error' || hasAttemptedSetCustomer) {
            return;
        }
        
        const sessionCustomerUid = customer.Uid;
        
        // If the session customer isn't the cart customer, set session customer to cart
        if (!isSessionCustomer) {
            setHasAttemptedSetCustomer(true);
            setCustomerToCart({
                urls: serviceUrls,
                domain,
                deviceCode,
                session,
                cartUid: cartUid ?? '',
                cartExternalId: encodedCartExternalId,
                cartSourceExternalId: cartSourceExternalId ?? '',
                customerUid: sessionCustomerUid
            });
        }
    }, [cartStatus, isSessionCustomer, customer, setCustomerStatus, hasAttemptedSetCustomer, serviceUrls, domain, deviceCode, cartUid, encodedCartExternalId, cartSourceExternalId, setCustomerToCart])
    
    // Reset attempt flag on success
    useEffect(() => {
        if (setCustomerStatus === 'success') {
            setHasAttemptedSetCustomer(false);
        }
    }, [setCustomerStatus])
    
    const showReferralSnippet = (e: SyntheticEvent) => {
        e.preventDefault()
        setShowReferral(true)
    }
    
    const applyCoupon = (code: string, couponUid?: string) => {
        if (hasCartIdentifiers) {
            // Set applying coupon code immediately for cart mode to show loading state
            setApplyingCouponCode(code);
            // Use cart API when cart identifiers are available
            applyToCart({
                urls: serviceUrls,
                domain,
                deviceCode,
                session,
                couponCode: code,
                couponUid: couponUid,
                quantity: 1,
                cartUid: cartUid ?? '',
                cartExternalId: encodedCartExternalId,
                cartSourceExternalId: cartSourceExternalId ?? ''
            })
        } else {
            // Use redeem API when no cart identifiers
            apply({urls: serviceUrls, domain, deviceCode, session, couponCode: code, couponUid: couponUid, quantity: 1})
        }
        setCouponCode(code)
    }
    
    const mapAccountIndicator = (accountIndicator: LoyaltyAccountIndicator, index: number) => {
        let accountClass = customer.LoyaltyPointsAccounts.find((account: any) => account.LoyaltyAccountClassUid === accountIndicator.ClassUid)
        if (!accountClass)
            accountClass = customer.LoyaltyCurrencyAccounts.find((account: any) => account.LoyaltyAccountClassUid === accountIndicator.ClassUid);
        
        if (!accountClass)
            return <></>
        
        switch (accountIndicator.Type) {
            case AccountIndicatorType.Value:
                return (
                    <div key={index} style={{width: '100%', maxWidth: '350px', boxSizing: 'border-box'}} className='bLoyal-center'>
                        <LoyaltyAccountIndicatorCard  accountIndicator={{Label: accountIndicator.Label, Value: accountClass.Balance}}/>
                        <br />
                    </div>
                )
            case AccountIndicatorType.ProgressBar:
                return (
                    <div key={index} style={{width: '100%', maxWidth: '550px', boxSizing: 'border-box'}} className='bLoyal-center'>
                    <h2>{accountIndicator.Label}</h2>
                        <ProgressBarWithCheckpoints useIcons={accountIndicator.ProgressBar.UseIcons} checkpoints={accountIndicator.ProgressBar.Checkpoints}  maximumValue={accountIndicator.ProgressBar.Maximum * 1.02} progress={accountClass.Balance} filledBackground={accountIndicator.ProgressBar.Color}/>
                        <br />
                        <br />
                    </div>
                )
            default:
                return <></>
        }
    }
    
   
    const getReferralSection = () => {
        if (props.snippetConfig.CustomerDashboard?.DisplayReferralSnippet && showReferral)
            return (
                <>
                    <ReferralSnippet snippetConfig={SnippetConfig} />
                    <br />
                    <br />
                </>
            )
        if (props.snippetConfig.CustomerDashboard?.DisplayReferralSnippet && !showReferral)
            return (
                <>
                    <button className='bl-snippet-button' id='show-referral-snippet' onClick={showReferralSnippet}>{props.snippetConfig.CustomerDashboard?.ReferralSnippetButtonText}</button>
                    <br />
                    <br />
                </>
            )
        
        return <></>
    }
    
    // If signup snippet should be shown, render it instead of dashboard
    // For embedded snippets, use SignupWrapper which uses existing AuthProvider
    if (showSignupSnippet && universalSignupSnippetCode) {
        return (
            <SignupWrapper snippetCode={universalSignupSnippetCode} setShowSignup={setShowSignupSnippet} />
        );
    }
    
    return (
        <div className='bLoyal-center' style={{padding: '0 10px'}}>
            <SnippetHeader logoUrl={props.snippetConfig.LogoUrl} title={props.snippetConfig.Title} message={props.snippetConfig.Message} snippetCode={props.snippetConfig.Code}/>
            {props.snippetConfig.CustomerDashboard?.DisplayQRCode &&
                <div className='bLoyal-qr-code'>
                    {/*@ts-ignore*/}
                    <QRCode value={qrValue === "Code" ? customer.Code : customer.LoyaltyCardNumber} size={100}/>
                    <p className='bLoyal-center'>{qrValue === "Code" ? customer.Code : customer.LoyaltyCardNumber}</p>
                </div>
            }
            <br />
            <>
                {props.snippetConfig.CustomerDashboard?.LoyaltyAccountIndicators.map(mapAccountIndicator)}
                {getReferralSection()}
                {status === "success" && dashboardProfile?.ShowCoupons === true && customerSession &&
                    <CouponSection 
                        snippetConfig={props.snippetConfig} 
                        customerSession={customerSession} 
                        applyCoupon={applyCoupon} 
                        onCompleteExists={onCompleteExists} 
                        isCartMode={hasCartIdentifiers}
                        applyToCartStatus={hasCartIdentifiers ? applyToCartStatus : undefined}
                        applyingCouponCode={hasCartIdentifiers ? applyingCouponCode : null}
                    />
                }
                {/*@ts-ignore*/}
                {applyStatus === 'error' && !hasCartIdentifiers && <p className={'bLoyal-error-text'}>{error.toString()}</p>}
                {/*@ts-ignore*/}
                {setCustomerStatus === 'error' && <p className={'bLoyal-error-text'}>Failed to update cart customer: {setCustomerError?.toString() ?? 'Unknown error'}</p>}
                <SnippetFooter footer={props.snippetConfig.Footer} snippetCode={props.snippetConfig.Code}/>
            </>
        </div>
    )
}